import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Commodity,
  IClasificacionPunto,
  IPuntoMedicion,
  IQueryParam,
  ISistema,
  RolRedGenerico,
} from 'modelos/src';
import { HttpService } from '../../auxiliares/http.service';
import { HelperService } from '../../auxiliares/helper.service';

/**
 * Nivel de red tal como lo publica el catálogo del backend. `orden` es propiedad
 * del catálogo y no del usuario: el backend rechaza un `orden` que no coincida
 * con el código, así que el front lo copia del catálogo y no lo edita.
 */
export interface INivelRedCatalogo {
  codigo: string;
  orden: number;
  etiqueta: string;
}

/**
 * Catálogos de los selectores, por servicio.
 *
 * **No se hardcodean en el front**: los sirve `GET /clasificacion/catalogos` y la
 * fuente única vive en gas-datos, que es donde además se validan. Duplicar la
 * lista acá es exactamente el problema que este proyecto vino a corregir.
 */
export interface ICatalogoClasificacion {
  commodity: Commodity;
  nivelesRed: INivelRedCatalogo[];
  tiposInstalacion: string[];
  subroles: string[];
  segmentosUsuario: string[];
  rolesRedGenericos: RolRedGenerico[];
}

/** Cobertura del ámbito del usuario. La devuelve `GET /clasificacion/cobertura`. */
export interface ICoberturaClasificacion {
  total: number;
  clasificados: number;
  coberturaPct: number;
  sinClasificar: number;
  /**
   * Cuántos no tienen segmento de usuario.
   *
   * Opcional porque lo agregó una versión posterior del backend: contra una API
   * vieja llega `undefined` y la tarjeta no se muestra, en vez de mostrar un 0
   * que sería mentira.
   */
  sinSegmento?: number;
  bloqueados: number;
  porDivision: Record<
    string,
    { total: number; clasificados: number; coberturaPct: number }
  >;
  porOrigen: Record<string, number>;
  porConfianza: Record<string, number>;
}

/**
 * Resultado de la clasificación masiva.
 *
 * El backend **no corta en el primer error**: devuelve un resultado por id. Que
 * un punto quede fuera del alcance de permisos del usuario no puede invalidar
 * los otros, y la UI tiene que mostrar cuál falló en vez de un "listo" genérico.
 */
export interface IResultadoBulkClasificacion {
  total: number;
  ok: number;
  fallidos: { id: string; motivo: string }[];
}

/** Tope de la selección masiva que impone el backend. */
export const MAX_BULK_CLASIFICACION = 500;

@Injectable({ providedIn: 'root' })
export class ClasificacionService {
  constructor(private http: HttpService) {}

  public getCobertura(
    queryParams?: IQueryParam,
  ): Observable<ICoberturaClasificacion> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`/puntosDeMedicion/clasificacion/cobertura`, {
      params,
    });
  }

  public getCatalogos(
    commodity: Commodity,
  ): Observable<ICatalogoClasificacion> {
    return this.http.get(`/puntosDeMedicion/clasificacion/catalogos`, {
      params: { commodity },
    });
  }

  /**
   * Clasificación manual de un punto. Queda **bloqueada** para el clasificador
   * automático: lo fuerza el backend, no hay forma de pedir lo contrario desde
   * acá, y es lo que garantiza que la corrección sobreviva a la corrida nocturna.
   */
  public clasificar(
    id: string,
    clasificacion: IClasificacionPunto,
  ): Observable<IPuntoMedicion> {
    return this.http.patch(`/puntosDeMedicion/${id}/clasificacion`, {
      clasificacion,
    });
  }

  public clasificarBulk(
    ids: string[],
    clasificacion: IClasificacionPunto,
  ): Observable<IResultadoBulkClasificacion> {
    return this.http.patch(`/puntosDeMedicion/clasificacion/bulk`, {
      ids,
      clasificacion,
    });
  }

  /** Los sistemas del cliente, para el selector de subsistema. */
  public getSistemas(): Observable<ISistema[]> {
    return this.http.get(`/red/sistemas`);
  }

  /**
   * Subsistema decidido a mano (`idSistema`; `null` = sin sistema), o de vuelta al
   * recálculo nocturno (`automatico`). Lo manual el recálculo no lo pisa.
   */
  public asignarSistemaBulk(
    ids: string[],
    cambio: { idSistema?: string | null; automatico?: boolean },
  ): Observable<IResultadoBulkClasificacion> {
    return this.http.patch(`/puntosDeMedicion/sistema/bulk`, {
      ids,
      ...cambio,
    });
  }
}
